/**
 * GFG-405 — Add gift links (spec AC 5–7): quantity → payment. No send-time step: Gifts for Good
 * sends nothing for a manual campaign. The quote is asked for on the payment step only, and the
 * payment review is the contacts wizard's own (`AddRecipientPaymentStep`, link-worded). Nothing is
 * written until ADD GIFT LINKS — the one call to `POST /campaigns/:campaignId/gift-links/additions`
 * (D9). Credits the wallet lacks are bought before that, in JR's Buy Credits modal; the write only
 * links that purchase. A moved quote (422) comes back to payment with the server's sentence and
 * fresh figures (the add's invalidation re-quotes).
 */
import type { PlanKey } from "@giftforward/entitlements";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { Dialog } from "@mui/material";
import { ThemeProvider } from "@mui/material/styles";
import {
  type Dispatch,
  type FormEvent,
  useEffect,
  useId,
  useReducer,
  useRef,
  useState,
} from "react";
import GfgButton from "@/components/ui/GfgButton";
import BuyCreditsModal from "@/components/usersOrgs/BuyCreditsModal";
import { COPY } from "@/constants/campaignContacts";
import { GIFT_LINKS_COPY } from "@/constants/giftLinks";
import { useAddGiftLinks, useGiftLinkAdditionsQuote } from "@/hooks/useGiftLinks";
import { useRecipientCap } from "@/hooks/useRecipientCap";
import type {
  CampaignContactsResponse,
  DepositPreference,
  RecipientAdditionsQuote,
} from "@/interfaces/campaignContacts";
import type { AddGiftLinksBody, AddGiftLinksResult } from "@/interfaces/giftLinks";
import { apiErrorMessage, apiErrorStatus } from "@/utils/apiError-helper";
import { clampToLinkCap } from "@/utils/link-quantity-helper";
import { formatUsd } from "@/utils/number-format";
import {
  capRefusalOpen,
  isCapRefusalFor,
  quantityWithinRoom,
  recipientRoom,
} from "@/utils/recipient-cap-helper";
import { resolveScheduleTimeZone } from "@/utils/timezone-helper";
import AddRecipientPaymentStep from "../contacts/AddRecipientPaymentStep";
import { aboveWizardDialog } from "../contacts/AddRecipientWizard";
import { CONTACTS_FORM_PAPER_SX, ContactsDialogHeader } from "../contacts/ContactsDialog";
import RecipientLimitDialog from "../contacts/RecipientLimitDialog";
import type { AdditionCreditsCopy, PaymentLineLabelOverrides } from "../contacts/types";
import { useAdditionCreditsPurchase } from "../contacts/useAdditionCreditsPurchase";
import GiftLinksQuantityStep from "./GiftLinksQuantityStep";
import { giftLinksCost, giftLinksCostLine } from "./giftLinks-helper";
import {
  addGiftLinksBody,
  giftLinksAddedToast,
  giftLinksPrimaryDisabled,
  giftLinksPrimaryLabel,
  giftLinksWizardReducer,
  giftLinksWizardTitle,
  initialGiftLinksWizardState,
} from "./giftLinksWizard-helper";
import type { GiftLinksWizardAction, GiftLinksWizardState } from "./types";

interface GiftLinksAddWizardProps {
  open: boolean;
  onClose: () => void;
  campaignId: string;
  envelope: CampaignContactsResponse;
  /** After the write (the only one), with the toast to show; the wizard then closes itself. */
  onAdded: (result: AddGiftLinksResult, message: string) => void;
}

type SubmitGiftLinks = (body: AddGiftLinksBody) => Promise<AddGiftLinksResult>;

/** `failed` needs a status; a request that never reached the server carries none. */
const NO_STATUS = 0;

const W = GIFT_LINKS_COPY.wizard;

/** The payment lines say "New gift links" where the contacts flow says "New gifts". */
const LINE_LABELS: PaymentLineLabelOverrides = { newGifts: W.lineNewLinks };

/**
 * AM4: the credits sentences worded for gift links. The payment step words the credit limit with
 * it; `useAdditionCreditsPurchase` words the shortfall sentences of its view with it.
 */
const CREDITS_COPY: AdditionCreditsCopy = {
  ...GIFT_LINKS_COPY.credits,
  creditLimit: GIFT_LINKS_COPY.creditLimit,
};

/** The server's refusal when it sent one; a request that never got an answer gets the retry line. */
function quoteFailure(error: unknown): string {
  return apiErrorStatus(error) === undefined ? W.quoteError : apiErrorMessage(error, W.quoteError);
}

// ---- hooks ---------------------------------------------------------------------------------------

/**
 * The write. A refusal goes back into the wizard with its HTTP status: the reducer decides the step
 * (a 422 returns to payment) and keeps any purchase made. Success hands the result up and closes.
 */
function useConfirm(i: {
  submit: SubmitGiftLinks;
  dispatch: Dispatch<GiftLinksWizardAction>;
  campaignName: string;
  onAdded: GiftLinksAddWizardProps["onAdded"];
  onClose: () => void;
  /** GFG-439: true when the error is a cap refusal for a finite-cap caller; opens the limit dialog. */
  onCapRefused: ((err: unknown) => boolean) | null;
}) {
  const inFlight = useRef(false);
  return async function confirm(body: AddGiftLinksBody): Promise<void> {
    if (inFlight.current) return;
    inFlight.current = true;
    i.dispatch({ type: "submit" });
    let result: AddGiftLinksResult;
    try {
      result = await i.submit(body);
    } catch (err) {
      const status = apiErrorStatus(err) ?? NO_STATUS;
      i.dispatch({ type: "failed", status, message: apiErrorMessage(err) });
      i.onCapRefused?.(err);
      return;
    } finally {
      inFlight.current = false;
    }
    i.onAdded(result, giftLinksAddedToast(result, i.campaignName));
    i.onClose();
  };
}

/**
 * The payment step's credits controls: the postpaid choice, and JR's Buy Credits modal for the
 * credits the wallet lacks. The modal's props come back apart; the wizard renders it.
 */
function useCreditsControls(i: {
  state: GiftLinksWizardState;
  dispatch: Dispatch<GiftLinksWizardAction>;
  quote: RecipientAdditionsQuote | undefined;
  timeZone: string;
  refetchQuote: () => void;
}) {
  const { state, dispatch } = i;
  const purchase = useAdditionCreditsPurchase({
    quote: i.quote,
    state,
    dispatch,
    timeZone: i.timeZone,
    refetchQuote: i.refetchQuote,
    creditsCopy: CREDITS_COPY,
  });
  const controls = {
    view: purchase.view,
    depositPreference: state.depositPreference,
    onPickDepositMode: (mode: DepositPreference) => dispatch({ type: "pickDepositMode", mode }),
    onBuy: purchase.openBuy,
    onCheckAgain: purchase.checkAgain,
    disabled: state.busy || purchase.busy,
  };
  return { controls, buyModal: purchase.buyModal };
}

/** Why the "Contact limit reached" dialog is up: a quantity above the room left, or a server refusal. */
type LimitReason = "quantity" | "refused";

/**
 * GFG-439 (D2): the quantity never exceeds the room left under the plan's cap (cap - the campaign's
 * enabled links); an attempt above it snaps back and opens the dialog. A quote or confirm the
 * server refuses for the cap opens it too. Only for a caller whose own cap is finite: staff on an
 * unlimited plan see the server's sentence in the wizard's own notice.
 */
function useLinkCap(c: {
  linkCount: number;
  qty: number;
  dispatch: Dispatch<GiftLinksWizardAction>;
  quoteError: unknown;
  cap: number | null;
  planKey: PlanKey | null;
}) {
  const { cap, planKey, dispatch } = c;
  const [reason, setReason] = useState<LimitReason | null>(null);
  const room = recipientRoom(cap, c.linkCount);
  const clamp = clampToLinkCap(c.qty, room !== null && room >= 1 ? room : null);
  const refusedOpen = capRefusalOpen({
    quoteError: c.quoteError,
    confirmRefused: reason === "refused",
    cap,
    planKey,
  });

  useEffect(() => {
    if (!clamp.overCap) return;
    dispatch({ type: "typeQty", text: String(clamp.value) });
    setReason("quantity");
  }, [clamp.overCap, clamp.value, dispatch]);

  return {
    cap,
    planKey,
    reason: refusedOpen ? "refused" : reason,
    close: () => setReason(null),
    onConfirmRefusal: (err: unknown) => {
      const hit = isCapRefusalFor(err, cap, planKey);
      if (hit) setReason("refused");
      return hit;
    },
  };
}

interface WizardBodyProps {
  titleId: string;
  campaignId: string;
  envelope: CampaignContactsResponse;
  submit: SubmitGiftLinks;
  onClose: () => void;
  onAdded: GiftLinksAddWizardProps["onAdded"];
}

/** The reducer, the quote it follows (payment step only), the credits controls and the submit. */
function useGiftLinksWizard(p: WizardBodyProps) {
  const { envelope } = p;
  const [state, dispatch] = useReducer(giftLinksWizardReducer, null, initialGiftLinksWizardState);
  const quoteBody = { count: state.qty, depositMode: state.depositPreference };
  const { cap, planKey } = useRecipientCap();
  // An over-room quantity snaps back (useLinkCap) and is never quoted; the server stays the judge
  // of anything within the room.
  const withinRoom = quantityWithinRoom(state.qty, recipientRoom(cap, envelope.contacts.length));
  const quoteQuery = useGiftLinkAdditionsQuote(
    p.campaignId,
    quoteBody,
    state.step === "payment" && withinRoom,
  );
  const quote = quoteQuery.data;
  // Another count's or deposit mode's quote keeps the previous figures up while it loads: they are
  // stale, so nothing moves on from them until the fresh quote is in.
  const quoteStale = quoteQuery.isFetching || quoteQuery.isPlaceholderData;
  const credits = useCreditsControls({
    state,
    dispatch,
    quote,
    timeZone: resolveScheduleTimeZone(envelope.campaign.scheduledTz),
    refetchQuote: quoteQuery.refetch,
  });
  const linkCap = useLinkCap({
    linkCount: envelope.contacts.length,
    qty: state.qty,
    dispatch,
    quoteError: quoteQuery.error,
    cap,
    planKey,
  });
  const confirm = useConfirm({
    submit: p.submit,
    dispatch,
    campaignName: envelope.campaign.name,
    onAdded: p.onAdded,
    onClose: p.onClose,
    onCapRefused: linkCap.onConfirmRefusal,
  });
  const disabled = giftLinksPrimaryDisabled({ state, quote, fetching: quoteStale });

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (disabled) return;
    if (state.step === "quantity") dispatch({ type: "next" });
    else if (quote !== undefined) void confirm(addGiftLinksBody(state, quote));
  }

  return {
    state,
    dispatch,
    quoteQuery,
    quote,
    quoteStale,
    credits,
    linkCap,
    disabled,
    handleSubmit,
  };
}

type Wizard = ReturnType<typeof useGiftLinksWizard>;

// ---- presentational pieces -----------------------------------------------------------------------

/** The current step's body: the quantity stepper or the payment review. */
function StepBody({ envelope, w }: { envelope: CampaignContactsResponse; w: Wizard }) {
  const { state, dispatch } = w;
  if (state.step === "quantity") {
    const pricePerGift = envelope.budget?.pricePerGift ?? 0;
    return (
      <GiftLinksQuantityStep
        qtyText={state.qtyText}
        costLine={giftLinksCostLine(state.qty, envelope.spacesFree, pricePerGift)}
        disabled={state.busy}
        onType={(text) => dispatch({ type: "typeQty", text })}
        onBlur={() => dispatch({ type: "blurQty" })}
        onStep={(delta) => dispatch({ type: "stepQty", delta })}
      />
    );
  }
  return (
    <AddRecipientPaymentStep
      quote={w.quote}
      loading={w.quoteStale}
      error={w.quoteQuery.isError ? quoteFailure(w.quoteQuery.error) : null}
      notice={state.notice}
      creditsPurchase={w.credits.controls}
      lineLabels={LINE_LABELS}
      creditsCopy={CREDITS_COPY}
    />
  );
}

function WizardFooter({
  primaryLabel,
  forward,
  busy,
  disabled,
  onCancel,
}: {
  primaryLabel: string;
  /** Another step follows: the button points onward. */
  forward: boolean;
  busy: boolean;
  disabled: boolean;
  onCancel: () => void;
}) {
  return (
    <div className="mt-2 flex flex-wrap items-center justify-end gap-3">
      <GfgButton type="button" variant="secondary" onClick={onCancel} disabled={busy}>
        {W.actions.cancel}
      </GfgButton>
      <GfgButton
        type="submit"
        variant="primary"
        disabled={disabled}
        endIcon={forward ? <ArrowForwardIcon sx={{ fontSize: 15 }} /> : undefined}
      >
        {primaryLabel}
      </GfgButton>
    </div>
  );
}

/** One run of the wizard: mounted when the dialog opens, so every open starts afresh. */
function WizardBody(props: WizardBodyProps) {
  const { titleId, envelope, onClose } = props;
  const w = useGiftLinksWizard(props);
  const { state } = w;
  const pricePerGift = envelope.budget?.pricePerGift ?? 0;
  const { paid } = giftLinksCost(state.qty, envelope.spacesFree, pricePerGift);
  const heading = giftLinksWizardTitle(
    state.step,
    state.qty,
    envelope.campaign.name,
    formatUsd(pricePerGift),
  );
  const primaryLabel = state.busy
    ? COPY.addWizard.actions.processing
    : giftLinksPrimaryLabel(state.step, paid);

  return (
    <>
      <form onSubmit={w.handleSubmit} noValidate className="flex flex-col gap-4">
        <ContactsDialogHeader
          titleId={titleId}
          title={heading.title}
          subtitle={heading.subtitle}
          onClose={() => state.busy || onClose()}
          compact
        />
        <StepBody envelope={envelope} w={w} />
        <WizardFooter
          primaryLabel={primaryLabel}
          forward={state.step === "quantity" && !state.busy}
          busy={state.busy}
          disabled={w.disabled}
          onCancel={onClose}
        />
      </form>
      {w.credits.buyModal !== null && (
        <ThemeProvider theme={aboveWizardDialog}>
          <BuyCreditsModal {...w.credits.buyModal} />
        </ThemeProvider>
      )}
      {w.linkCap.cap !== null && w.linkCap.planKey !== null && (
        <RecipientLimitDialog
          open={w.linkCap.reason !== null}
          cap={w.linkCap.cap}
          planKey={w.linkCap.planKey}
          // A quantity above the room only snaps back; a server refusal ends the wizard.
          onClose={w.linkCap.reason === "quantity" ? w.linkCap.close : onClose}
        />
      )}
    </>
  );
}

export default function GiftLinksAddWizard({
  open,
  onClose,
  campaignId,
  envelope,
  onAdded,
}: GiftLinksAddWizardProps) {
  const titleId = useId();
  const addGiftLinks = useAddGiftLinks(campaignId);

  return (
    <Dialog
      open={open}
      onClose={() => addGiftLinks.isPending || onClose()}
      maxWidth={false}
      aria-labelledby={titleId}
      slotProps={{ paper: { sx: CONTACTS_FORM_PAPER_SX } }}
    >
      <WizardBody
        titleId={titleId}
        campaignId={campaignId}
        envelope={envelope}
        submit={addGiftLinks.mutateAsync}
        onClose={onClose}
        onAdded={onAdded}
      />
    </Dialog>
  );
}
